import { type FC, useCallback } from "react";
import ValidatedForm from "components/forms/ValidatedForm";
import { type AutosaveFormProps } from "interfaces";
import { type ExternalLinkFormData } from "../types/externalLinkTypes";

export interface ExternalLinkFormProps {
  disabled: boolean;
  isLoading?: boolean;
  disablePreventTransitionPrompt?: boolean;
  isReadOnlyView: boolean;
}

export type ConfigurationFormProps = ExternalLinkFormProps & AutosaveFormProps<ExternalLinkFormData>;

const ExternalLinkForm: FC<ConfigurationFormProps> = (props) => {
  const { dirty, isLoading, disablePreventTransitionPrompt, isReadOnlyView } = props;

  const getValidateFieldProps = useCallback(() => {
    return {
      errors: props.errors,
      touched: props.touched,
      isFormValid: props.isValid,
      handleChange: props.handleChange,
      handleBlur: props.handleBlur,
      setFieldValue: props.setFieldValue,
      setFieldTouched: props.setFieldTouched,
      dirty: props.dirty,
      resetForm: props.resetForm,
    };
  }, [props]);

  const renderFormFields = useCallback(() => {
    const { disabled, values, onBlur } = props;
    const validatedFieldProps = getValidateFieldProps();

    return (
      <div>
        <ValidatedForm.InputField
          {...validatedFieldProps}
          propertyName="title"
          value={values.title}
          label="Title"
          markAsRequired
          disabled={disabled}
          onBlur={onBlur}
          placeholder="Enter link title"
          setFieldValue={props.setFieldValue}
          isReadOnlyView={isReadOnlyView}
        />
        <ValidatedForm.TextAreaField
          {...validatedFieldProps}
          propertyName="description"
          value={values.description}
          label="Description"
          markAsRequired
          disabled={disabled}
          onBlur={onBlur}
          placeholder="Enter link description"
          rows="3"
          setFieldValue={props.setFieldValue}
          isReadOnlyView={isReadOnlyView}
        />
        <ValidatedForm.InputField
          {...validatedFieldProps}
          propertyName="url"
          value={values.url}
          label="Link"
          markAsRequired
          inputType="url"
          disabled={disabled}
          onBlur={onBlur}
          placeholder="Enter link URL"
          setFieldValue={props.setFieldValue}
          isReadOnlyView={isReadOnlyView}
        />
        <ValidatedForm.InputField
          {...validatedFieldProps}
          propertyName="urlText"
          value={values.urlText}
          label="Button Label"
          markAsRequired
          disabled={disabled}
          onBlur={onBlur}
          placeholder="Enter label for the link button"
          setFieldValue={props.setFieldValue}
          isReadOnlyView={isReadOnlyView}
        />
      </div>
    );
  }, [getValidateFieldProps, props, isReadOnlyView]);

  return (
    <ValidatedForm
      disablePreventTransitionPrompt={disablePreventTransitionPrompt}
      unsavedChangesPrompt={{
        title: "Exit Without Saving?",
        message: "Are you sure you want to exit without saving this link? All information entered will be lost.",
      }}
      dirty={dirty}
      isLoading={isLoading}
      formWidthComputer={6}
      withoutFormik
    >
      {renderFormFields}
    </ValidatedForm>
  );
};

export default ExternalLinkForm;
